import { Box, Chip, Stack, Typography } from '@mui/material'
import { C1Button } from 'c1ui'
import { DeleteConfirmationDialogWithTextInput } from 'components/core/confirmation/dialog/delete'
import { C1CopyText } from 'components/core/copytext/C1CopyText'
import { EmptyList } from 'components/core/empty-list'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { C1TableReduxHook } from 'components/core/list/C1Table/C1TableReduxHook'
import { PageHeader } from 'components/core/page-header'
import { RelativeTimestamp } from 'components/core/time/display'
import { useDrawer } from 'hooks/useDrawer'
import { KeySquare } from 'lucide-react'
import type { ServicePrincipalCredential } from 'pbts/c1/api/service_principal/v1/service_principal'
import { ServicePrincipalServiceListCredentialsRequest, ServicePrincipalServiceRevokeCredentialRequest } from 'pbts/c1/api/service_principal/v1/service_principal'
import type { Timestamp } from 'pbts/google/protobuf/timestamp'
import { FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { listServicePrincipalCredentialsApi, revokeServicePrincipalCredentialApi } from 'redux_toolkit/servicePrincipals'
import { CreateSPCredentialDrawer } from './CreateSPCredentialDrawer'

type SPCredentialsTabProps = {
    userId: string
}

const isExpiringSoon = (expiresAt: Timestamp | undefined): boolean => {
    if (!expiresAt) return false
    const expMs = expiresAt.seconds * 1000
    const fourteenDays = 14 * 24 * 60 * 60 * 1000
    return expMs > Date.now() && expMs - Date.now() < fourteenDays
}

const isExpired = (expiresAt: Timestamp | undefined): boolean => {
    if (!expiresAt) return false
    return expiresAt.seconds * 1000 < Date.now()
}

const cols: C1TableReduxColumn<ServicePrincipalCredential>[] = [
    {
        field: 'displayName',
        label: <FormattedMessage id="sp_cred_col_name" defaultMessage="Name" />,
        Component: ({ row }): React.ReactElement => (
            <Typography>{row.displayName}</Typography>
        ),
    },
    {
        field: 'clientId',
        label: <FormattedMessage id="sp_cred_col_client_id" defaultMessage="Client ID" />,
        Component: ({ row }): React.ReactElement => (
            <C1CopyText value={row.clientId} typographyVariant="monospace" />
        ),
    },
    {
        field: 'createdAt',
        label: <FormattedMessage id="sp_cred_col_created" defaultMessage="Created" />,
        Component: ({ row }): React.ReactElement => (
            <RelativeTimestamp variant="date_only" date={row.createdAt} />
        ),
    },
    {
        field: 'expiresAt',
        label: <FormattedMessage id="sp_cred_col_expires" defaultMessage="Expires" />,
        Component: ({ row }): React.ReactElement => (
            <Stack direction="row" spacing={1} alignItems="center">
                <RelativeTimestamp variant="date_only" date={row.expiresAt} />
                {isExpired(row.expiresAt) && <Chip label="Expired" color="error" size="small" />}
                {!isExpired(row.expiresAt) && isExpiringSoon(row.expiresAt) && <Chip label="Expiring soon" color="warning" size="small" />}
            </Stack>
        ),
    },
    {
        field: 'lastUsedAt',
        label: <FormattedMessage id="sp_cred_col_last_used" defaultMessage="Last used" />,
        Component: ({ row }): React.ReactElement => (
            row.lastUsedAt
                ? <RelativeTimestamp date={row.lastUsedAt} />
                : <Typography color="text.secondary">
                    <FormattedMessage id="sp_cred_never_used" defaultMessage="Never" />
                </Typography>
        ),
    },
]

export const SPCredentialsTab: FC<SPCredentialsTabProps> = ({ userId }) => {
    const intl = useIntl()
    const [showDrawer, hideDrawer] = useDrawer()
    const { mutate: revokeCredential, inProgress: revokeInProgress } = revokeServicePrincipalCredentialApi()
    const [revokeTarget, setRevokeTarget] = useState<ServicePrincipalCredential | null>(null)

    const request = useMemo(() => ServicePrincipalServiceListCredentialsRequest.create({
        servicePrincipalId: userId,
    }), [userId])

    const handleCreate = useCallback((): void => {
        showDrawer(
            <CreateSPCredentialDrawer close={hideDrawer} servicePrincipalId={userId} />,
            { variant: 'sm' },
        )
    }, [showDrawer, hideDrawer, userId])

    const handleRevoke = useCallback(async (): Promise<void> => {
        if (!revokeTarget || revokeInProgress) return
        await revokeCredential(ServicePrincipalServiceRevokeCredentialRequest.create({
            servicePrincipalId: userId,
            id: revokeTarget.id,
        }))
        setRevokeTarget(null)
    }, [revokeTarget, revokeInProgress, revokeCredential, userId])

    const revokeCol: C1TableReduxColumn<ServicePrincipalCredential> = useMemo(() => ({
        field: 'actions',
        label: '',
        Component: ({ row }): React.ReactElement => (
            <C1Button
                variant="outlined"
                color="error"
                onClick={(e: React.MouseEvent): void => {
                    e.stopPropagation()
                    setRevokeTarget(row)
                }}
            >
                <FormattedMessage id="sp_cred_revoke" defaultMessage="Revoke" />
            </C1Button>
        ),
    }), [])

    const allCols = useMemo(() => [...cols, revokeCol], [revokeCol])

    return <>
        <PageHeader
            title={intl.formatMessage({ defaultMessage: 'Credentials', id: 'sp_credentials_title' })}
            actions={
                <C1Button onClick={handleCreate} variant="contained">
                    <FormattedMessage id="sp_credential_create_button" defaultMessage="Create credential" />
                </C1Button>
            }
        />
        <Box sx={{ mt: 2 }}>
            <C1TableReduxHook<ServicePrincipalServiceListCredentialsRequest, ServicePrincipalCredential>
                cols={allCols}
                request={request}
                hook={listServicePrincipalCredentialsApi.useListCredentials}
                invalidateHook={listServicePrincipalCredentialsApi.useInvalidateListCredentials}
                hookFlags={{ notify: true, autoReload: true }}
                emptyState={
                    <EmptyList
                        title={<FormattedMessage id="sp_credentials_empty" defaultMessage="No credentials" />}
                        text={<FormattedMessage id="sp_credentials_empty_text" defaultMessage="Create a credential to enable API access for this service principal." />}
                        icon={<C1Icon icon={KeySquare} />}
                    />
                }
            />
        </Box>
        {revokeTarget && (
            <DeleteConfirmationDialogWithTextInput
                open={!!revokeTarget}
                itemName={revokeTarget.displayName}
                details={
                    <FormattedMessage
                        id="sp_credential_revoke_warning"
                        defaultMessage="Revoking this credential will immediately prevent any workloads using it from authenticating. This action cannot be undone."
                    />
                }
                onConfirm={handleRevoke}
                onCancel={(): void => setRevokeTarget(null)}
                isActionLoading={revokeInProgress}
            />
        )}
    </>
}
